import { ACTIVE_MEMBER_UPDATE_MUTATION } from 'apollo/mutations/member-mutations';
import { useActiveMember } from 'hooks/store';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { authMemberSetAction, StoreAuthMemberProper } from 'store/store';
import { IMemberUpdateInput } from 'types';
import useMemberUpdate from './useMemberUpdate';

export type TUseActiveMemberUpdateOptions = {
  optimisticUpdate?: boolean;
};

export default function useActiveMemberUpdate() {
  const [updateKey, update] = useMemberUpdate(ACTIVE_MEMBER_UPDATE_MUTATION);

  const activeMember = useActiveMember();
  const dispatch = useDispatch();

  const updateMemberKey = useCallback(
    async <Key extends keyof IMemberUpdateInput>(
      key: Key,
      value: IMemberUpdateInput[Key],
      options?: TUseActiveMemberUpdateOptions
    ) => {
      try {
        if (options?.optimisticUpdate) {
          const member: StoreAuthMemberProper = { ...activeMember, [key]: value };
          dispatch(authMemberSetAction(member));
        }

        const result = await updateKey(activeMember.id!, key, value);
        dispatch(authMemberSetAction(result.data!.updateMember!));
        return result;
      } catch (e) {
        dispatch(authMemberSetAction(activeMember));
        return null;
      }
    },
    [updateKey, activeMember, dispatch]
  );

  const updateMember = useCallback(
    async (value: Omit<IMemberUpdateInput, 'id'>, options?: TUseActiveMemberUpdateOptions) => {
      try {
        if (options?.optimisticUpdate) {
          const member: StoreAuthMemberProper = { ...activeMember, ...value };
          dispatch(authMemberSetAction(member));
        }

        const result = await update({ ...value, id: activeMember.id! });
        dispatch(authMemberSetAction(result.data!.updateMember!));
        return result;
      } catch (e) {
        dispatch(authMemberSetAction(activeMember));
        return null;
      }
    },
    [update, activeMember, dispatch]
  );

  return [updateMemberKey, updateMember] as const;
}
